import {
  builderAccountPath,
  builderAutoRendersPath,
  builderHomePagePath,
  builderOptionCatalogPath,
  builderPlanLibraryPath,
  builderUsersPath,
  marketingPath,
  studioFilesPath,
  useAppRouterParams,
} from "lib/routeHelpers";
import { userCanUseStudio, userCanViewAutoRenders } from "lib/security/client";
import { useUserContext } from "lib/state/UserContextProvider";
import Link from "ui/components/Link";
import cx from "ui/cx";
import IconAutoRenders from "ui/icons/IconAutoRenders";
import IconBuilderDashboard from "ui/icons/IconBuilderDashboard";
import IconGear from "ui/icons/IconGear";
import IconMarketing from "ui/icons/IconMarketing";
import IconOptionCatalog from "ui/icons/IconOptionCatalog";
import IconPlanLibrary from "ui/icons/IconPlanLibrary";
import IconStudioFiles from "ui/icons/IconStudioFiles";
import IconUsersFour from "ui/icons/IconUsersFour";
import styles from "./BuilderPortalSideNav.module.scss";
import { Locations } from "./Locations";

export type BuilderPortalPage =
  | "dashboard"
  | "studioFiles"
  | "plans"
  | "locations"
  | "autoRenders"
  | "marketing"
  | "options"
  | "users"
  | "settings";

export default function BuilderPortalSideNav({ selected }: { selected?: BuilderPortalPage }) {
  const { builderId, locationId } = useAppRouterParams();
  const userContext = useUserContext();
  const { hasPermission, user, enabledFeatures, builder } = userContext;

  if (!builderId) {
    return null;
  }

  return (
    <nav className={styles.wrapper}>
      <ul>
        <li className={cx({ styles, link: true, selected: selected === "dashboard" })}>
          <Link href={builderHomePagePath(builderId)}>
            <IconBuilderDashboard /> Dashboard
          </Link>
        </li>
        {hasPermission("VIEW_MASTER_PLANS") && (
          <li className={cx({ styles, selected: selected === "plans" && locationId === undefined })}>
            <Link href={builderPlanLibraryPath(builderId)}>
              <IconPlanLibrary /> Plan Library
            </Link>
          </li>
        )}
        {userCanUseStudio(userContext) && (
          <li className={cx({ styles, selected: selected === "studioFiles" })}>
            <Link href={studioFilesPath(builderId)}>
              <IconStudioFiles /> Studio Files
            </Link>
          </li>
        )}
        {enabledFeatures.nextGenOptions && user && hasPermission("VIEW_OPTION_CATALOG") && (
          <li className={cx({ styles, selected: selected === "options" })}>
            <Link href={builderOptionCatalogPath(builderId)}>
              <IconOptionCatalog /> Option Catalog
            </Link>
          </li>
        )}
        {hasPermission("VIEW_SALES_CATEGORIES") && (
          <li className={cx({ styles, selected: selected === "marketing" })}>
            <Link href={marketingPath(builderId)}>
              <IconMarketing /> Marketing
            </Link>
          </li>
        )}
        {userCanViewAutoRenders({ hasPermission, builder, enabledFeatures }) && (
          <li className={cx({ styles, selected: selected === "autoRenders" })}>
            <Link href={builderAutoRendersPath(builderId)}>
              <IconAutoRenders /> AutoRender
            </Link>
          </li>
        )}
      </ul>
      {hasPermission("VIEW_COMMUNITIES") && <Locations builderId={builderId} selected={selected === "locations"} />}
      <ul className={styles.bottomLeftNav}>
        {hasPermission("VIEW_STAFF_PERMISSIONS") && (
          <li className={cx({ styles, selected: selected === "users" })}>
            <Link href={builderUsersPath(builderId)}>
              <IconUsersFour /> User Management
            </Link>
          </li>
        )}
        <li className={cx({ styles, link: true, selected: selected === "settings" })}>
          <Link href={builderAccountPath(builderId)}>
            <IconGear /> Settings
          </Link>
        </li>
      </ul>
    </nav>
  );
}
